import { useMemo, useState } from "react";

import { Button, EmptyState, Kpi, PageHead } from "@/components/common";
import { Guard } from "@/auth/Guard";
import { useAuth } from "@/auth/useAuth";
import { InviteModal } from "@/components/invitations/InviteModal";
import { useActiveWorkspace } from "@/contexts/WorkspaceContext";
import { useClients } from "@/hooks/useClients";
import { useOrganizations } from "@/hooks/useOrganizations";
import { OrgCreateModal } from "./OrgCreateModal";
import { OrganizationsList } from "./OrganizationsList";

export function OrganizationsPage() {
  const { activeWorkspaceId } = useActiveWorkspace();
  const { user } = useAuth();
  const activeWorkspaceName = user?.manageableWorkspaces.find(
    (w) => w.id === activeWorkspaceId,
  )?.name;
  const { data: orgs = [] } = useOrganizations(activeWorkspaceId);
  const { data: clients = [] } = useClients(activeWorkspaceId);
  const [inviting, setInviting] = useState(false);
  const [creating, setCreating] = useState(false);

  const stats = useMemo(() => {
    const ativas = orgs.filter((o) => o.active).length;
    const media = orgs.length ? (clients.length / orgs.length).toFixed(1) : "0";
    return { total: orgs.length, ativas, empresas: clients.length, media };
  }, [orgs, clients]);

  if (activeWorkspaceId == null) {
    return (
      <>
        <PageHead title="Organizações" subtitle="Pessoas jurídicas (CNPJ) do workspace" />
        <EmptyState icon="layers" title="Selecione um workspace" />
      </>
    );
  }

  return (
    <>
      <PageHead
        title="Organizações"
        subtitle={`${stats.total} organizações · ${stats.empresas} empresas vinculadas`}
        actions={
          <>
            <Guard permission="organizations.invitations.create">
              <Button variant="ghost" iconLeft="users" onClick={() => setInviting(true)}>
                Convidar membros
              </Button>
            </Guard>
            <Guard permission="organizations.write">
              <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
                Criar organização
              </Button>
            </Guard>
          </>
        }
      />

      <div className="kpi-grid kpi-grid-3">
        <Kpi
          icon="layers"
          tone="featured"
          label="Organizações"
          value={String(stats.total)}
          sub={`${stats.ativas} ativas`}
        />
        <Kpi
          icon="building"
          label="Empresas vinculadas"
          value={String(stats.empresas)}
          sub={`média ${stats.media} CNPJs por organização`}
        />
        <Kpi
          icon="shield-check"
          label="Ativas"
          value={String(stats.ativas)}
          sub={`${stats.total - stats.ativas} inativas`}
        />
      </div>

      <OrganizationsList workspaceId={activeWorkspaceId} hideCreate />

      {inviting && (
        <InviteModal
          kind="organization"
          workspaceId={activeWorkspaceId}
          onClose={() => setInviting(false)}
        />
      )}
      {creating && (
        <OrgCreateModal
          workspaceId={activeWorkspaceId}
          workspaceName={activeWorkspaceName}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}
